import React, { useState, useEffect, useCallback } from 'react';
import {
  Ticket, Plus, Loader2, Trash2, Save, Eye, EyeOff, Upload,
  ChevronDown, ChevronUp, Users, DollarSign, Calendar, Clock,
  AlertCircle, CheckCircle, XCircle, Package
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  draft: { bg: 'bg-white/5 text-white/40', label: 'Draft' },
  scheduled: { bg: 'bg-indigo-400/10 text-indigo-400', label: 'Scheduled' },
  on_sale: { bg: 'bg-green-400/10 text-green-400', label: 'On Sale' },
  sold_out: { bg: 'bg-amber-400/10 text-amber-400', label: 'Sold Out' },
  live: { bg: 'bg-red-400/10 text-red-400', label: 'Live' },
  completed: { bg: 'bg-white/5 text-white/30', label: 'Completed' },
  canceled: { bg: 'bg-red-400/10 text-red-400', label: 'Canceled' },
};

const VIPEventsAdmin = ({ token }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/vip-events`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setEvents(await r.json());
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const createEvent = async () => {
    setCreating(true);
    try {
      const r = await fetch(`${API}/api/admin/vip-events`, {
        method: 'POST', headers,
        body: JSON.stringify({ title: 'New VIP Event' }),
      });
      if (r.ok) {
        const ev = await r.json();
        toast.success('Event created');
        setExpanded(ev.id);
        load();
      }
    } catch { toast.error('Failed to create event'); }
    setCreating(false);
  };

  const deleteEvent = async (id) => {
    if (!window.confirm('Delete this VIP event?')) return;
    try {
      const r = await fetch(`${API}/api/admin/vip-events/${id}`, {
        method: 'DELETE', headers,
      });
      if (r.ok) {
        toast.success('Event removed');
        load();
      }
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div data-testid="vip-events-admin">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-white font-bold text-lg flex items-center gap-2">
          <Ticket size={18} className="text-amber-400" /> VIP Events
        </h2>
        <button
          onClick={createEvent}
          disabled={creating}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-400/10 border border-amber-400/20 text-amber-400 text-[10px] font-bold hover:bg-amber-400/20 transition-colors disabled:opacity-50"
          data-testid="vip-create-event"
        >
          {creating ? <Loader2 size={10} className="animate-spin" /> : <Plus size={10} />}
          New Event
        </button>
      </div>
      <p className="text-white/30 text-xs mb-5">Create and manage premium VIP Stage Pass events.</p>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 size={24} className="text-amber-400 animate-spin" /></div>
      ) : events.length === 0 ? (
        <div className="glass-card p-8 text-center">
          <Ticket size={32} className="text-white/10 mx-auto mb-3" />
          <p className="text-white/25 text-sm font-medium">No VIP Events</p>
          <p className="text-white/15 text-xs mt-1">Create your first premium event to start selling VIP passes.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {events.map(event => (
            <EventCard
              key={event.id}
              event={event}
              expanded={expanded === event.id}
              onToggle={() => setExpanded(expanded === event.id ? null : event.id)}
              onDelete={() => deleteEvent(event.id)}
              onRefresh={load}
              token={token}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const EventCard = ({ event, expanded, onToggle, onDelete, onRefresh, token }) => {
  const st = STATUS_STYLES[event.status] || STATUS_STYLES.draft;

  return (
    <div className="glass-card overflow-hidden" data-testid={`vip-admin-${event.id}`}>
      {/* Header Row */}
      <div
        className="flex items-center gap-3 p-4 cursor-pointer hover:bg-white/[0.02] transition-colors"
        onClick={onToggle}
        data-testid={`vip-toggle-${event.id}`}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`px-2 py-0.5 rounded text-[9px] font-bold ${st.bg}`}>{st.label}</span>
            <h3 className="text-white text-sm font-bold truncate">{event.title}</h3>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-white/20 text-[10px]">
            {event.event_date && (
              <span>{new Date(event.event_date).toLocaleDateString()}</span>
            )}
            <span>{event.passes_sold || 0} sold</span>
            <span>${((event.revenue_cents || 0) / 100).toFixed(0)} revenue</span>
            {event.waitlist_count > 0 && <span>{event.waitlist_count} waitlisted</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="p-1.5 text-white/20 hover:text-red-400 hover:bg-red-400/10 rounded transition-colors"
            data-testid={`vip-delete-${event.id}`}
          >
            <Trash2 size={14} />
          </button>
          {expanded ? <ChevronUp size={16} className="text-white/20" /> : <ChevronDown size={16} className="text-white/20" />}
        </div>
      </div>

      {/* Expanded Editor */}
      {expanded && (
        <EventEditor event={event} onRefresh={onRefresh} token={token} />
      )}
    </div>
  );
};

const EventEditor = ({ event, onRefresh, token }) => {
  const [form, setForm] = useState({
    title: event.title || '',
    guest_name: event.guest_name || '',
    description: event.description || '',
    event_date: event.event_date ? event.event_date.slice(0, 16) : '',
    event_end_date: event.event_end_date ? event.event_end_date.slice(0, 16) : '',
    event_timezone: event.event_timezone || 'America/New_York',
    status: event.status || 'draft',
    waitlist_enabled: event.waitlist_enabled || false,
    marquee_enabled: event.marquee_enabled !== false,
    tiers: event.tiers || [],
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [ordersExpanded, setOrdersExpanded] = useState(false);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [linking, setLinking] = useState(false);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const isValidSquareId = (id) => id && typeof id === 'string' && id.trim().length >= 10;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        event_date: form.event_date ? new Date(form.event_date).toISOString() : null,
        event_end_date: form.event_end_date ? new Date(form.event_end_date).toISOString() : null,
      };
      const r = await fetch(`${API}/api/admin/vip-events/${event.id}`, {
        method: 'PUT', headers,
        body: JSON.stringify(payload),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        // Check if auto-linking happened
        const linkResult = d._square_link_result;
        if (linkResult && linkResult.linked > 0) {
          toast.success(`Saved & linked ${linkResult.linked} tier(s) to Square`);
        } else if (linkResult && linkResult.errors?.length > 0) {
          toast.warning(`Saved, but ${linkResult.errors.length} tier(s) failed Square linkage`);
        } else {
          toast.success('Event saved');
        }
        onRefresh();
      } else {
        toast.error(d.detail || 'Save failed');
      }
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  const retryLink = async () => {
    setLinking(true);
    try {
      const r = await fetch(`${API}/api/admin/vip-events/${event.id}/link-square`, {
        method: 'POST', headers,
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        if (d.linked > 0) {
          toast.success(`Linked ${d.linked} tier(s) to Square`);
        } else if (d.already_linked > 0 && d.linked === 0) {
          toast.info('All tiers already linked');
        } else if (d.errors?.length > 0) {
          toast.error(`Linkage failed: ${d.errors.map(e => e.error).join('; ')}`);
        }
        onRefresh();
      } else {
        toast.error(d.detail || 'Link failed');
      }
    } catch { toast.error('Link failed'); }
    setLinking(false);
  };

  const uploadImage = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API}/api/admin/vip-events/${event.id}/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (r.ok) {
        toast.success('Image uploaded');
        onRefresh();
      } else {
        toast.error('Upload failed');
      }
    } catch { toast.error('Upload failed'); }
    setUploading(false);
  };

  const loadOrders = async () => {
    setOrdersLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/vip-events/${event.id}/orders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setOrders(await r.json());
    } catch {}
    setOrdersLoading(false);
  };

  const toggleOrders = () => {
    if (!ordersExpanded) loadOrders();
    setOrdersExpanded(!ordersExpanded);
  };

  // Tier management
  const addTier = () => {
    set('tiers', [...form.tiers, {
      tier_id: crypto.randomUUID ? crypto.randomUUID() : `tier-${Date.now()}`,
      name: 'General VIP',
      price_cents: 0,
      currency: 'USD',
      total_inventory: 50,
      remaining: 50,
      square_variation_id: '',
      square_item_id: '',
    }]);
  };

  const updateTier = (idx, key, val) => {
    const updated = [...form.tiers];
    updated[idx] = { ...updated[idx], [key]: val };
    // Keep remaining in sync with total when editing a draft
    if (key === 'total_inventory' && event.status === 'draft') {
      updated[idx].remaining = val;
    }
    set('tiers', updated);
  };

  const removeTier = (idx) => {
    set('tiers', form.tiers.filter((_, i) => i !== idx));
  };

  const STATUSES = ['draft', 'scheduled', 'on_sale', 'live', 'completed', 'canceled'];

  return (
    <div className="border-t border-white/5 p-5 space-y-5" data-testid={`vip-editor-${event.id}`}>
      {/* Basic Info */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className="text-white/40 text-[10px] uppercase tracking-wider block mb-1">Event Title</label>
          <input
            value={form.title}
            onChange={e => set('title', e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-amber-400/30"
            data-testid="vip-input-title"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="text-white/40 text-[10px] uppercase tracking-wider block mb-1">Guest / Celebrity</label>
          <input
            value={form.guest_name}
            onChange={e => set('guest_name', e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-amber-400/30"
            placeholder="e.g. Kevin Hart"
            data-testid="vip-input-guest"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="text-white/40 text-[10px] uppercase tracking-wider block mb-1">Description</label>
          <textarea
            value={form.description}
            onChange={e => set('description', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-amber-400/30 resize-none"
            data-testid="vip-input-description"
          />
        </div>
      </div>

      {/* Date/Time */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="text-white/40 text-[10px] uppercase tracking-wider flex items-center gap-1 mb-1">
            <Calendar size={10} /> Start Date/Time
          </label>
          <input
            type="datetime-local"
            value={form.event_date}
            onChange={e => set('event_date', e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-amber-400/30 [color-scheme:dark]"
            data-testid="vip-input-date"
          />
        </div>
        <div>
          <label className="text-white/40 text-[10px] uppercase tracking-wider flex items-center gap-1 mb-1">
            <Clock size={10} /> End Date/Time
          </label>
          <input
            type="datetime-local"
            value={form.event_end_date}
            onChange={e => set('event_end_date', e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-amber-400/30 [color-scheme:dark]"
            data-testid="vip-input-end-date"
          />
        </div>
        <div>
          <label className="text-white/40 text-[10px] uppercase tracking-wider block mb-1">Timezone</label>
          <select
            value={form.event_timezone}
            onChange={e => set('event_timezone', e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-amber-400/30 [color-scheme:dark]"
          >
            <option value="America/New_York">Eastern</option>
            <option value="America/Chicago">Central</option>
            <option value="America/Denver">Mountain</option>
            <option value="America/Los_Angeles">Pacific</option>
            <option value="UTC">UTC</option>
          </select>
        </div>
      </div>

      {/* Image Upload */}
      <div>
        <label className="text-white/40 text-[10px] uppercase tracking-wider block mb-2">
          <Upload size={10} className="inline mr-1" /> Promotional Image
        </label>
        <div className="flex items-center gap-4">
          {event.image_url && (
            <img src={event.image_url} alt="Event" className="w-20 h-20 rounded-lg object-cover border border-white/10" />
          )}
          <label className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white/40 text-xs cursor-pointer hover:bg-white/10 transition-colors">
            {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            {uploading ? 'Uploading...' : 'Upload Image'}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={e => e.target.files[0] && uploadImage(e.target.files[0])}
              data-testid="vip-upload-image"
            />
          </label>
        </div>
      </div>

      {/* Status + Toggles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="text-white/40 text-[10px] uppercase tracking-wider block mb-1">Status</label>
          <select
            value={form.status}
            onChange={e => set('status', e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-amber-400/30 [color-scheme:dark]"
            data-testid="vip-input-status"
          >
            {STATUSES.map(s => (
              <option key={s} value={s}>{(STATUS_STYLES[s] || {}).label || s}</option>
            ))}
          </select>
        </div>
        <div className="flex items-end gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.waitlist_enabled}
              onChange={e => set('waitlist_enabled', e.target.checked)}
              className="w-4 h-4 accent-amber-400"
              data-testid="vip-toggle-waitlist"
            />
            <span className="text-white/50 text-xs">Waitlist</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.marquee_enabled}
              onChange={e => set('marquee_enabled', e.target.checked)}
              className="w-4 h-4 accent-amber-400"
              data-testid="vip-toggle-marquee"
            />
            <span className="text-white/50 text-xs">Marquee</span>
          </label>
        </div>
      </div>

      {/* Pricing Tiers */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-white/40 text-[10px] uppercase tracking-wider flex items-center gap-1">
            <DollarSign size={10} /> Pricing Tiers
          </label>
          <button
            onClick={addTier}
            className="flex items-center gap-1 text-amber-400 text-[10px] font-bold hover:text-amber-300 transition-colors"
            data-testid="vip-add-tier"
          >
            <Plus size={10} /> Add Tier
          </button>
        </div>
        {form.tiers.length === 0 ? (
          <p className="text-white/15 text-xs py-3">No tiers configured. Add at least one tier before publishing.</p>
        ) : (
          <div className="space-y-3">
            {form.tiers.map((tier, idx) => (
              <div key={tier.tier_id} className="p-3 rounded-lg bg-white/[0.02] border border-white/5 space-y-2" data-testid={`vip-tier-editor-${idx}`}>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div>
                    <label className="text-white/30 text-[9px] block mb-0.5">Name</label>
                    <input
                      value={tier.name}
                      onChange={e => updateTier(idx, 'name', e.target.value)}
                      className="w-full px-2 py-1.5 rounded bg-white/5 border border-white/10 text-white text-xs focus:outline-none focus:border-amber-400/30"
                      data-testid={`tier-name-${idx}`}
                    />
                  </div>
                  <div>
                    <label className="text-white/30 text-[9px] block mb-0.5">Price ($)</label>
                    <input
                      type="number"
                      value={tier.price_cents / 100}
                      onChange={e => updateTier(idx, 'price_cents', Math.round(parseFloat(e.target.value || 0) * 100))}
                      className="w-full px-2 py-1.5 rounded bg-white/5 border border-white/10 text-white text-xs focus:outline-none focus:border-amber-400/30"
                      data-testid={`tier-price-${idx}`}
                      step="0.01"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="text-white/30 text-[9px] block mb-0.5">Inventory (-1 = unlimited)</label>
                    <input
                      type="number"
                      value={tier.total_inventory}
                      onChange={e => updateTier(idx, 'total_inventory', parseInt(e.target.value || -1))}
                      className="w-full px-2 py-1.5 rounded bg-white/5 border border-white/10 text-white text-xs focus:outline-none focus:border-amber-400/30"
                      data-testid={`tier-inventory-${idx}`}
                      min="-1"
                    />
                  </div>
                  <div className="flex items-end gap-1">
                    {tier.total_inventory !== -1 && (
                      <div className="flex-1">
                        <label className="text-white/30 text-[9px] block mb-0.5">Remaining</label>
                        <span className="text-amber-400 text-xs font-bold">{tier.remaining}</span>
                      </div>
                    )}
                    <button
                      onClick={() => removeTier(idx)}
                      className="p-1.5 text-white/20 hover:text-red-400 hover:bg-red-400/10 rounded transition-colors"
                      data-testid={`tier-remove-${idx}`}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
                {isValidSquareId(tier.square_variation_id) ? (
                  <div className="flex items-center gap-1 text-green-400/60 text-[9px]" data-testid={`tier-linked-${idx}`}>
                    <CheckCircle size={9} /> Square linked
                  </div>
                ) : tier.price_cents > 0 ? (
                  <div className="flex items-center gap-2" data-testid={`tier-unlinked-${idx}`}>
                    <span className="flex items-center gap-1 text-amber-400/60 text-[9px]">
                      <AlertCircle size={9} /> Not linked to Square
                    </span>
                    <button
                      onClick={() => retryLink()}
                      disabled={linking}
                      className="flex items-center gap-1 px-2 py-0.5 rounded bg-amber-400/10 border border-amber-400/20 text-amber-400 text-[8px] font-bold hover:bg-amber-400/20 transition-colors disabled:opacity-50"
                      data-testid={`tier-retry-link-${idx}`}
                    >
                      {linking ? <Loader2 size={8} className="animate-spin" /> : <Package size={8} />}
                      {linking ? 'Linking...' : 'Retry Link'}
                    </button>
                    <span className="text-white/20 text-[8px]">or Save to auto-link</span>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Orders Section */}
      <div>
        <button
          onClick={toggleOrders}
          className="flex items-center gap-2 text-white/30 text-xs font-semibold hover:text-white/50 transition-colors"
          data-testid="vip-toggle-orders"
        >
          <Users size={12} />
          {ordersExpanded ? 'Hide' : 'View'} Orders ({event.passes_sold || 0})
        </button>

        {ordersExpanded && (
          <div className="mt-3 space-y-1.5">
            {ordersLoading ? (
              <Loader2 size={16} className="text-amber-400 animate-spin" />
            ) : orders.length === 0 ? (
              <p className="text-white/15 text-xs">No orders yet.</p>
            ) : (
              orders.map(o => {
                const statusColor = o.status === 'COMPLETED' ? 'text-green-400' :
                  o.status === 'REFUNDED' ? 'text-red-400' :
                  o.status === 'OVERSOLD' ? 'text-amber-400' : 'text-white/30';
                return (
                  <div key={o.id} className="flex items-center justify-between p-2 rounded-lg bg-white/[0.02] border border-white/5 text-[10px]">
                    <div>
                      <span className={`font-bold uppercase ${statusColor}`}>{o.status}</span>
                      <span className="text-white/20 ml-2">{o.tier_name}</span>
                      <span className="text-white/15 ml-2">${((o.price_cents || 0) / 100).toFixed(0)}</span>
                    </div>
                    <span className="text-white/15">{new Date(o.created_at).toLocaleString()}</span>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="flex items-center gap-3 pt-2 border-t border-white/5">
        <button
          onClick={save}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-black text-xs font-bold uppercase tracking-wider hover:from-amber-400 hover:to-amber-500 transition-all disabled:opacity-50"
          data-testid="vip-save-event"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {saving ? 'Saving & Linking...' : 'Save Event'}
        </button>
        <span className="text-white/20 text-[9px]">
          Tiers are automatically linked to Square on save
        </span>
      </div>
    </div>
  );
};

export default VIPEventsAdmin;
